import type { Chapter45Anchor } from '../../chapter45';

/**
 * Extract product ID from the characteristic URL
 */
export function extractProductId(url: string): string | null {
  const match = url.match(/medicinal-products\/(\d+)/);
  return match?.[1] ?? null;
}

/**
 * Check if a characteristic URL has PDF content available
 */
export function hasPdfContent(url: string | undefined): boolean {
  return !!url;
}

/**
 * Create a unique key for an anchor to track first/last status globally
 */
export function anchorKey(a: Chapter45Anchor): string {
  return `${a.blockId}:${a.start}:${a.end}`;
}

/**
 * Determine which anchors are first/last within their quote group across all blocks.
 * Returns a Set of anchor keys that are first and a Set that are last.
 */
export function computeQuoteFirstLast(anchors: Chapter45Anchor[]): {
  firstAnchors: Set<string>;
  lastAnchors: Set<string>;
} {
  const anchorsByQuote = new Map<number, Chapter45Anchor[]>();
  for (const a of anchors) {
    const qi = a.quoteIndex ?? 0;
    const list = anchorsByQuote.get(qi) ?? [];
    list.push(a);
    anchorsByQuote.set(qi, list);
  }

  const firstAnchors = new Set<string>();
  const lastAnchors = new Set<string>();

  for (const [_qi, quoteAnchors] of anchorsByQuote.entries()) {
    if (quoteAnchors.length === 0) continue;

    quoteAnchors.sort((a, b) => {
      const blockCmp = a.blockId.localeCompare(b.blockId);
      if (blockCmp !== 0) return blockCmp;
      return a.start - b.start;
    });

    firstAnchors.add(anchorKey(quoteAnchors[0]!));
    lastAnchors.add(anchorKey(quoteAnchors[quoteAnchors.length - 1]!));
  }

  return { firstAnchors, lastAnchors };
}

/**
 * Build a map of anchors grouped by block ID
 */
export function buildRangesMap(
  anchors: Chapter45Anchor[],
): Map<string, Chapter45Anchor[]> {
  const map = new Map<string, Chapter45Anchor[]>();

  for (const a of anchors) {
    const list = map.get(a.blockId) ?? [];
    list.push(a);
    map.set(a.blockId, list);
  }

  for (const [blockId, list] of map.entries()) {
    list.sort((a, b) => a.start - b.start);
    map.set(blockId, list);
  }

  return map;
}

/**
 * Strip curly braces from text.
 * LLM sometimes uses {text} to indicate highlighted portions which we need to remove.
 */
export function stripCurlyBraces(text: string): string {
  return text.replace(/[{}｛｝﹛﹜❴❵[\]]/g, '');
}

/**
 * Decode common HTML entities to their actual characters
 */
export function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&#(\d+);/g, (_, num) => String.fromCharCode(parseInt(num, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16)),
    );
}

/**
 * Unescape markdown escape sequences (e.g., \* → *, \\ → \)
 */
export function unescapeMarkdown(text: string): string {
  const BACKSLASH_PLACEHOLDER = '\uE000';

  return text
    .replace(/\\\\/g, BACKSLASH_PLACEHOLDER)
    .replace(/\\\*/g, '*')
    .replace(/\\_/g, '_')
    .replace(/\\#/g, '#')
    .replace(/\\`/g, '`')
    .replace(/\\~/g, '~')
    .replace(/\\>/g, '>')
    .replace(/\\</g, '<')
    .replace(/\\-/g, '-')
    .replace(/\\!/g, '!')
    .replace(/\\\[/g, '[')
    .replace(/\\\]/g, ']')
    .replace(/\\\(/g, '(')
    .replace(/\\\)/g, ')')
    .replace(new RegExp(BACKSLASH_PLACEHOLDER, 'g'), '\\');
}
